import { Injectable } from '@angular/core';

import { SportModel } from '@frontend/sports/betting-offer/feature/model';
import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { TournamentsDataConfig } from '@frontend/sports/common/client-config-data-access';
import { CountItem, ItemType, children } from '@frontend/sports/common/core/data-access/sport-model';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { CompetitionRoute } from '@frontend/sports/navigation/core/feature/models';
import { SportUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { EventSection, LeagueItem, LeagueSection, Navigation, Tournament, TournamentSection } from '@frontend/sports/types/components/tournaments';
import { DeviceService } from '@frontend/vanilla/core';
import { assign, flatMap, property, uniq } from 'lodash-es';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';

import { CompetitionRouteService } from '../../competition-list/competition-route.service';
import { FixtureList } from '../sport/competitions/competition.models';
import { EventListService } from '../sport/services/event-list.service';
import { TournamentEventSection, TournamentLeague, TournamentViewModel } from './tournament.models';

export interface TournamentFixtureList extends FixtureList {
    tournament?: TournamentViewModel;
}

@Injectable({ providedIn: 'root' })
export class TournamentService {
    constructor(
        private tournamentsConfig: TournamentsDataConfig,
        private routeService: CompetitionRouteService,
        private eventService: EventListService,
        private urlHelper: UrlHelperService,
        private deviceService: DeviceService,
        private sportCache: SportsCacheService,
    ) {}

    getTournament(local: boolean = false, name?: string): Observable<TournamentViewModel | undefined> {
        if (name && this.tournamentsConfig.tournaments) {
            const byName = (current: Tournament) => current.navigation && current.navigation.name === name;
            const tournament = this.tournamentsConfig.tournaments.find(byName);

            if (tournament) {
                return this.mapTournament(tournament, local);
            }
        }

        return of(undefined);
    }

    getFixtureList(name: string, league?: number, params?: CompetitionRoute): Observable<TournamentFixtureList | undefined> {
        return this.getTournament(true, name).pipe(
            switchMap((tournament) => {
                const empty: TournamentFixtureList = {
                    tournament,
                    fixtures: [],
                    totalCount: 0,
                };

                if (!tournament || !league) {
                    return of(empty);
                }

                return this.eventService.getFixtureList(params || this.routeService.params()).pipe(map((result) => assign(empty, result)));
            }),
        );
    }

    getPreResolveFixtureList(params: CompetitionRoute, name: string, league?: number): Observable<TournamentFixtureList | undefined> {
        return this.getFixtureList(name, league, params);
    }

    private mapTournament(tournament: Tournament, local: boolean = false): Observable<TournamentViewModel> {
        const tomorrow = this.getTomorrowFormatted();

        return forkJoin([
            this.getSection(tournament.liveSection, tournament, { to: tomorrow }, local),
            this.getSection(tournament.preMatchSection, tournament, { from: tomorrow }, local),
            this.sportCache.getSport(tournament.leaguesSection.sport.id),
        ]).pipe(map(([live, preMatch, sport]) => this.mapModel(tournament, live, preMatch, sport)));
    }

    private mapModel(
        tournament: Tournament,
        live?: TournamentEventSection,
        preMatch?: TournamentEventSection,
        sport?: CountItem,
    ): TournamentViewModel {
        const baseSection = { visible: false, title: '' };
        const base: Navigation = tournament.navigation || {
            name: '',
            icon: '',
            quickLink: baseSection,
            filter: baseSection,
            breadcrumb: baseSection,
        };
        const filter: TournamentSection = base.filter || baseSection;
        const section: LeagueSection = tournament.leaguesSection;

        return {
            id: base.name || '',
            name: filter.title || '',
            sections: {
                live,
                preMatch,
                leagues: {
                    visible: section.visible,
                    title: section.title || '',
                },
            },
            leagues: this.mapLeagues(tournament, sport),
            sport: this.mapSport(sport)!,
            icon: base.icon,
        };
    }

    private getSection(
        section: EventSection,
        tournament: Tournament,
        leagueInterval: { from?: string; to?: string },
        local: boolean = false,
    ): Observable<TournamentEventSection | undefined> {
        if (!section || !tournament || !tournament.leaguesSection) {
            return of(undefined);
        }

        const result = {} as TournamentEventSection;
        let leagues: number[] = [];

        if (tournament.leaguesSection.leagues) {
            leagues = flatMap(tournament.leaguesSection.leagues, (league) => [league.preMatch, league.live]).filter(isDefined);
            leagues = uniq(leagues);
        }

        if (local || leagues.length === 0) {
            return of({
                ...result,
                title: section.title,
                visible: section.visible,
                count: section.eventCount,
            });
        }

        return this.eventService
            .getFixtureList({ ...this.routeService.params(), league: leagues }, undefined, undefined, section.eventCount, leagueInterval)
            .pipe(
                map((output) => ({
                    ...result,
                    title: section.title,
                    visible: section.visible,
                    leagues,
                    events: output ? output.fixtures : [],
                    count: section.eventCount,
                })),
            );
    }

    private mapLeagues(tournament: Tournament, sport?: CountItem): TournamentLeague[] {
        if (!tournament.navigation || !tournament.leaguesSection.leagues) {
            return [];
        }

        return tournament.leaguesSection.leagues.map((league) => ({
            id: {
                live: league.live || 0,
                preMatch: league.preMatch || 0,
            },
            name: league.name,
            region: league.region,
            participants: league.participants || [],
            url: this.getLeagueUrl(league, tournament),
            count: this.getLeagueCount(league, sport),
        }));
    }

    private mapSport(sport?: CountItem): SportModel | undefined {
        if (!sport) {
            return;
        }

        return {
            id: sport.id,
            name: sport.name,
            virtual: property<CountItem, boolean>('isVirtual')(sport),
        };
    }

    private getLeagueUrl(league: LeagueItem, tournament: Tournament): string {
        const sport = tournament.leaguesSection.sport;
        const name = tournament.navigation.name ?? '';

        const urlParam = {
            id: league.preMatch || league.live || 0,
            name: league.name,
        };

        return this.deviceService.isRobot
            ? this.urlHelper.getSportUrl(sport, SportUrlParam.Betting, league.region, urlParam)
            : this.urlHelper.getTournamentUrl(sport, name, urlParam);
    }

    private getLeagueCount(league: LeagueItem, sport?: CountItem): number {
        if (!sport) {
            return 0;
        }

        const match = children(sport, ItemType.Competition).find((item) => item.id === league.preMatch || item.id === league.live);

        if (match) {
            return match.counts.live + match.counts.preMatch;
        }

        return 0;
    }

    private getTomorrowFormatted(): string {
        let today = new Date();
        today = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);

        const tomorrow = new Date(today);
        tomorrow.setMinutes(today.getMinutes() + 24 * 60);

        return tomorrow.toISOString().substring(0, 19);
    }
}
